// ExamenParent.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  Info
} from 'lucide-react';

// Import des composants
// Un seul composant pour les trois examens : ce qui les distingue tient dans
// examensConfig. Les trois anciens fichiers, quasi identiques, avaient derive
// chacun de leur cote.
import SectionExamen from './SectionExamen';
import { EXAMENS_NATIONAUX } from '../config/examensConfig';
import { formaterFCFA } from '../utils/montants';
import FilArianeExamen from './examens/FilArianeExamen';
import CandidatsLibresSection from './CandidatsLibresSection';
import ResultatsDevenir from './examens/ResultatsDevenir';

// Import du hook personnalisé
import { useExamens } from '../hooks/useExamens';
// Import du contexte
import { ExamensProvider, useExamensContext } from '../contexts/ExamensContext';


/**
 * Porte d'entree vers un examen.
 *
 * Elle remplace a la fois la carte recapitulative et l'onglet correspondant :
 * les chiffres etaient deja la, il ne manquait que le clic.
 */
const CarteExamen = ({ titre, sousTitre, lignes, onClick, pied = null }) => (
  <Card
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    className="cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#246990]/30"
  >
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center justify-between text-lg text-[#246990]">
        {titre}
        <ChevronRight size={18} className="text-gray-300" />
      </CardTitle>
      <CardDescription>{sousTitre}</CardDescription>
    </CardHeader>
    <CardContent>
      {lignes.length > 0 ? (
        <div className="space-y-2">
          {lignes.map((l) => (
            <div key={l.libelle} className="flex justify-between text-sm">
              <span className="text-gray-600">{l.libelle} :</span>
              <span className={`font-semibold tabular-nums ${l.ton || 'text-gray-900'}`}>
                {l.valeur}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm leading-snug text-gray-500">{pied}</p>
      )}
    </CardContent>
  </Card>
);

/**
 * Les quatre compteurs du tableau de bord.
 *
 * Degrades de la palette KLASYS, dans l'ordre du module Ressources Humaines :
 * bleu principal, teal, saumon, puis le gris fonce des valeurs de reference.
 */
const STATISTIQUES = [
  {
    cle: 'total',
    titre: "Total d'eleves",
    sousTitre: 'Inscrits aux examens',
    icone: Users,
    couleur: 'from-[#246990] to-[#3084b5]',
    valeur: (t) => t.totalEleves
  },
  {
    cle: 'complets',
    titre: 'Dossiers complets',
    sousTitre: 'Prets pour validation',
    icone: CheckCircle,
    couleur: 'from-[#319190] to-[#63b5b4]',
    valeur: (t) => t.totalComplets
  },
  {
    cle: 'attente',
    titre: 'En attente',
    sousTitre: 'Pieces a verifier',
    icone: Clock,
    couleur: 'from-[#c66d59] to-[#ae7467]',
    valeur: (t) => t.totalEnAttente
  },
  {
    cle: 'incomplets',
    titre: 'Incomplets',
    sousTitre: 'Pieces manquantes',
    icone: AlertCircle,
    couleur: 'from-gray-800 to-gray-700',
    valeur: (t) => t.totalIncomplets
  }
];

// ============================================================================
// COMPOSANT PRINCIPAL (CONTENU)
// ============================================================================
const ExamenParentContent = () => {
  const { getStatsDashboard, loading } = useExamens();
  const { refreshTrigger } = useExamensContext();

  // null = tableau de bord. Sinon le code de l'examen ouvert, ou
  // 'CANDIDATS_LIBRES'. La tablist a cede la place a ce drill-in : elle
  // occupait une barre permanente pour des ecrans qui, faute de contenu,
  // s'ouvraient sur une page vide.
  const [examenOuvert, setExamenOuvert] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    cepe: { total: 0, complets: 0, en_attente: 0, incomplets: 0 },
    bepc: { total: 0, complets: 0, en_attente: 0, incomplets: 0 },
    bac: { total: 0, complets: 0, en_attente: 0, incomplets: 0 },
    paiements: { total_attendu: 0, total_paye: 0, nombre_payes: 0, nombre_non_payes: 0 },
    total_eleves: 0
  });

  // Fonction pour charger les données
  const fetchDashboardData = async () => {
    try {
      console.log('📊 Chargement des statistiques du dashboard...');
      const stats = await getStatsDashboard();
      if (stats) {
        setDashboardData(stats);
        console.log('✅ Dashboard rechargé:', stats);
      }
    } catch (error) {
      console.error("❌ Erreur chargement dashboard:", error);
    }
  };

  // Charger au montage du composant
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Recharger quand refreshTrigger change
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('🔄 Rafraîchissement automatique déclenché');
      fetchDashboardData();
    }
  }, [refreshTrigger]);

  // Calculer les totaux - VERSION CORRIGÉE
  const totalEleves = dashboardData.total_eleves || 0;

  // Conversion en nombre avec parseInt pour éviter les bugs
  const totalComplets = parseInt(dashboardData.cepe?.complets || 0) +
    parseInt(dashboardData.bepc?.complets || 0) +
    parseInt(dashboardData.bac?.complets || 0);

  const totalEnAttente = parseInt(dashboardData.cepe?.en_attente || 0) +
    parseInt(dashboardData.bepc?.en_attente || 0) +
    parseInt(dashboardData.bac?.en_attente || 0);

  const totalIncomplets = parseInt(dashboardData.cepe?.incomplets || 0) +
    parseInt(dashboardData.bepc?.incomplets || 0) +
    parseInt(dashboardData.bac?.incomplets || 0);

  // Calculer les stats financières
  const totalAttendu = parseFloat(dashboardData.paiements?.total_attendu || 0);
  const totalPaye = parseFloat(dashboardData.paiements?.total_paye || 0);
  const tauxRecouvrement = totalAttendu > 0 ? ((totalPaye / totalAttendu) * 100).toFixed(1) : 0;
  const resteAPayer = totalAttendu - totalPaye;

  // Debug logs
  console.log('📈 Stats calculées:', {
    totalEleves,
    totalComplets,
    totalEnAttente,
    totalIncomplets,
    cepe: dashboardData.cepe,
    bepc: dashboardData.bepc,
    bac: dashboardData.bac
  });

  const retourTableauBord = () => setExamenOuvert(null);

  if (examenOuvert === 'CANDIDATS_LIBRES') {
    return (
      <div className="w-full p-4">
        <FilArianeExamen
          codeCourant="CANDIDATS_LIBRES"
          libelleCourant="Candidats libres"
          onRetour={retourTableauBord}
          onChangerExamen={setExamenOuvert}
        />
        <CandidatsLibresSection />
      </div>
    );
  }

  if (examenOuvert === 'RESULTATS_DEVENIR') {
    return (
      <div className="w-full p-4">
        <ResultatsDevenir onRetour={retourTableauBord} onChangerExamen={setExamenOuvert} />
      </div>
    );
  }

  if (examenOuvert && EXAMENS_NATIONAUX[examenOuvert]) {
    return (
      <div className="w-full p-4">
        <SectionExamen
          examen={EXAMENS_NATIONAUX[examenOuvert]}
          onRetourTableauBord={retourTableauBord}
          onChangerExamen={setExamenOuvert}
        />
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <h1 className="text-3xl font-bold mb-6 text-[#246990]">Examens Nationaux</h1>

      {/* Statistiques principales.
              Meme motif que le module Ressources Humaines : degrades de la
              palette KLASYS, valeur en text-2xl. Les couleurs precedentes
              (#28a745, #ffc107, #dc3545) venaient de Bootstrap et ne figurent
              nulle part dans la charte. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        {STATISTIQUES.map((stat) => (
          <Card key={stat.cle} className="border-0 shadow-md overflow-hidden">
            <div className={`bg-gradient-to-r ${stat.couleur} text-white p-4`}>
              <CardTitle className="text-sm font-medium flex items-center gap-1 mb-2">
                <stat.icone size={16} className="text-white" />
                <span className="text-xs leading-tight">{stat.titre}</span>
              </CardTitle>
              <CardDescription className="text-white/80 text-xs hidden md:block">
                {stat.sousTitre}
              </CardDescription>
              {loading ? (
                <div className="h-12 flex items-center justify-center">
                  <div className="animate-pulse h-3 bg-white/30 rounded w-3/4"></div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center mt-2">
                  <span className="text-2xl font-bold tabular-nums">
                    {stat.valeur({ totalEleves, totalComplets, totalEnAttente, totalIncomplets })}
                  </span>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Carte Financière */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-[#246990]">Suivi Financier des Frais d'Examen</CardTitle>
          <CardDescription>Récapitulatif des paiements</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total attendu</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {formaterFCFA(totalAttendu)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total recouvré</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {formaterFCFA(totalPaye)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reste à payer</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-orange-600">
                {formaterFCFA(resteAPayer)}
              </p>
            </div>
            {/* Le seul chiffre en gras fort : il resume les trois autres. */}
            <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Taux de recouvrement</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-[#246990]">
                {tauxRecouvrement}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Portes d'entrée.
              Ces cartes affichaient déjà les bons chiffres ; elles deviennent
              cliquables et remplacent la tablist. Une quatrième accueille les
              candidats libres, qui n'ont pas de tableau de bord propre. */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mt-6">
        {Object.values(EXAMENS_NATIONAUX).map((ex) => {
          const stats = dashboardData[ex.code.toLowerCase()] || {};
          return (
            <CarteExamen
              key={ex.code}
              titre={ex.libelleLong}
              sousTitre={`Classes de ${ex.libelleClasses}`}
              onClick={() => setExamenOuvert(ex.code)}
              lignes={[
                { libelle: "Total d'élèves", valeur: stats.total || 0 },
                { libelle: 'Dossiers complets', valeur: stats.complets || 0, ton: 'text-green-600' },
                { libelle: 'En attente', valeur: stats.en_attente || 0, ton: 'text-amber-600' },
                { libelle: 'Incomplets', valeur: stats.incomplets || 0, ton: 'text-red-600' }
              ]}
            />
          );
        })}

        <CarteExamen
          titre="Candidats libres"
          sousTitre="Hors établissement"
          onClick={() => setExamenOuvert('CANDIDATS_LIBRES')}
          lignes={[]}
          pied="Inscrire et suivre un candidat qui ne vient pas de l'école."
        />
      </div>

      {/* Après les examens : qui les a eus, qui est resté. Une bande pleine
          largeur plutôt qu'une cinquième carte, qui casserait la grille. */}
      <Card
        onClick={() => setExamenOuvert('RESULTATS_DEVENIR')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExamenOuvert('RESULTATS_DEVENIR');
          }
        }}
        className="mt-6 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#246990]/30"
      >
        <CardContent className="flex items-center justify-between gap-4 p-5">
          <div>
            <p className="text-lg font-semibold text-[#246990]">Résultats et devenir</p>
            <p className="text-sm text-gray-500">
              Taux de réussite de l&apos;école, admis restés ou partis, taux de réinscription réel hors bacheliers.
            </p>
          </div>
          <ChevronRight size={18} className="flex-shrink-0 text-gray-300" />
        </CardContent>
      </Card>

      {/* Ou va l'argent encaisse ici.
              Le dire a l'ecran evite d'avoir a le decouvrir : jusqu'a ce
              chantier, les frais d'examens n'entraient dans AUCUNE caisse. */}
      <div className="mt-6 flex items-start gap-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
        <Info size={18} className="mt-0.5 flex-shrink-0 text-[#246990]" />
        <div className="text-sm leading-relaxed text-gray-700">
          <p className="font-semibold text-[#246990]">Où vont les frais encaissés ici ?</p>
          <p className="mt-1">
            Chaque versement est enregistré dans la caisse de la personne qui l'encaisse.
            Il apparaît dans son portefeuille, sous <span className="font-semibold">Frais d'examens nationaux</span>,
            et compte dans ses encaissements du jour.
          </p>
          <p className="mt-1 text-[11px] text-gray-500">
            La caisse du jour doit être ouverte pour pouvoir encaisser.
          </p>
        </div>
      </div>

    </div>
  );
};

// ============================================================================
// WRAPPER AVEC PROVIDER
// ============================================================================
const ExamenParent = () => {
  return (
    <ExamensProvider>
      <ExamenParentContent />
    </ExamensProvider>
  );
};

export default ExamenParent;